import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { NotificationList } from '@/types/api'
import { toast } from '@/hooks/use-toast'

const API_BASE = import.meta.env.VITE_API_URL || ''

async function fetchNotifications(limit = 15): Promise<NotificationList> {
  const token = localStorage.getItem('access_token')
  const response = await fetch(`${API_BASE}/api/notifications?limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch notifications')
  return response.json()
}

async function fetchUnreadCount(): Promise<{ unread_count: number }> {
  const token = localStorage.getItem('access_token')
  const response = await fetch(`${API_BASE}/api/notifications/unread-count`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch unread count')
  return response.json()
}

async function markNotificationRead(notificationId: string): Promise<void> {
  const token = localStorage.getItem('access_token')
  await fetch(`${API_BASE}/api/notifications/${notificationId}/read`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
  })
}

async function markAllNotificationsRead(): Promise<void> {
  const token = localStorage.getItem('access_token')
  await fetch(`${API_BASE}/api/notifications/read-all`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
  })
}

export function useNotifications() {
  const queryClient = useQueryClient()

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchNotifications(),
    refetchInterval: false,
  })

  const unreadCount = useQuery({
    queryKey: ['unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: false,
  })

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unread-count'] })
    },
  })

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unread-count'] })
    },
  })

  useEffect(() => {
    let source: EventSource | null = null

    function openStream() {
      const currentToken = localStorage.getItem('access_token')
      if (!currentToken) return

      source = new EventSource(
        `${API_BASE}/api/notifications/stream?token=${currentToken}`
      )

      source.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data)
          queryClient.invalidateQueries({ queryKey: ['notifications'] })
          queryClient.invalidateQueries({ queryKey: ['unread-count'] })

          if (['reply_received', 'bounce'].includes(notification.event_type)) {
            toast({
              title: notification.title,
              description: notification.message,
              variant: notification.event_type === 'bounce' ? 'destructive' : 'default',
            })
          }

          if (notification.event_type === 'sync_completed') {
            queryClient.invalidateQueries({ queryKey: ['contacts'] })
            queryClient.invalidateQueries({ queryKey: ['segments'] })
            toast({
              title: notification.title,
              description: notification.message,
              variant: 'success',
            })
          }

          if (notification.event_type === 'sync_failed') {
            toast({
              title: notification.title,
              description: notification.message,
              variant: 'destructive',
            })
          }
        } catch {
          // Ignore parse errors (heartbeats are comments, not data)
        }
      }

      source.onerror = () => {
        console.warn('SSE connection error, will auto-reconnect')
      }
    }

    function closeStream() {
      if (source) {
        source.close()
        source = null
      }
    }

    const handleSessionExpired = () => closeStream()
    const handleSessionRestored = () => {
      closeStream()
      openStream()
    }

    window.addEventListener('session-expired', handleSessionExpired)
    window.addEventListener('session-restored', handleSessionRestored)

    openStream()

    return () => {
      closeStream()
      window.removeEventListener('session-expired', handleSessionExpired)
      window.removeEventListener('session-restored', handleSessionRestored)
    }
  }, [queryClient])

  return {
    notifications: notifications.data?.items ?? [],
    total: notifications.data?.total ?? 0,
    unreadCount: unreadCount.data?.unread_count ?? 0,
    isLoading: notifications.isLoading,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
    refetch: notifications.refetch,
  }
}
